import { lazy, Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { IconChevronLeft, IconExternalLink, IconHelp, IconX } from '@/lib/icons';
import { EmptyState } from '@/components/shared/EmptyState';
import { SkeletonCard } from '@/components/shared/SkeletonCard';
import { HELP_ARTICLES } from '@/content/help/_index';
import { HELP_TIER_LABELS } from '@/content/help/types';
import type { HelpArticleMeta, HelpSearchEntry } from '@/content/help/types';
import { helpSlugsForPath } from '@/lib/helpPageMap';
import { useHelpDrawer } from '@/components/help/HelpDrawerContext';
import { HelpSearchInput } from '@/components/help/HelpSearchInput';
import { AdminBadge } from '@/components/help/mdx-components';
import { useReducedMotion } from '@/hooks/useReducedMotion';

// HelpArticle pulls in the runtime MDX evaluator (@mdx-js/mdx), which is heavy
// and only needed once the operator actually opens an article in the drawer.
// The drawer itself is mounted on every page, so lazy-load the reader to keep
// the MDX runtime out of the always-loaded chrome bundle.
const HelpArticle = lazy(() =>
  import('@/components/help/HelpArticle').then((m) => ({ default: m.HelpArticle })),
);

// K0.9: the page-relevant view shows at most a handful of cards; dense routes
// (e.g. the draft editor) map many slugs, so the overflow is surfaced via a
// "View all" link rather than a long scroll.
const MAX_CONTEXTUAL_ARTICLES = 5;

interface ArticleCardProps {
  article: HelpArticleMeta;
  onSelect: () => void;
}

function ArticleCard({ article, onSelect }: ArticleCardProps): JSX.Element {
  return (
    <button
      type="button"
      onClick={onSelect}
      className="w-full text-left rounded-md border border-dash-border bg-dash-bg/40 hover:bg-dash-bg p-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/60 transition-colors"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="text-sm font-medium text-dash-primary line-clamp-2">
          {article.title}
        </div>
        {article.adminOnly ? <AdminBadge /> : null}
      </div>
      <div className="mt-1 text-xs text-dash-secondary line-clamp-2">{article.summary}</div>
      <div className="mt-2">
        <span className="inline-flex items-center bg-accent/10 text-accent rounded px-2 py-0.5 text-[10px] uppercase tracking-wide font-mono">
          {HELP_TIER_LABELS[article.tier]}
        </span>
      </div>
    </button>
  );
}

export function ContextualHelpDrawer(): JSX.Element | null {
  const { isOpen, openedFromSlug, closeDrawer } = useHelpDrawer();
  const location = useLocation();
  const navigate = useNavigate();
  const reduced = useReducedMotion();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<HelpSearchEntry[]>([]);
  const searchInputRef = useRef<HTMLDivElement>(null);
  const [mounted, setMounted] = useState(false);
  // selectedSlug drives the in-drawer reading view. When set, the drawer swaps
  // the article list for the rendered article instead of routing to /help/:slug
  // — keeping the operator on their current page.
  const [selectedSlug, setSelectedSlug] = useState<string | null>(null);

  // Trigger the slide-in transition on mount, and reset transient state on close.
  useEffect(() => {
    if (isOpen) {
      // If the drawer was opened pointed at a specific article (onboarding's
      // openDrawer(slug) / openOnceForKey), open straight into the reader.
      setSelectedSlug(openedFromSlug ?? null);
      // Delay one tick so the initial render is "closed" -> "open".
      const id = window.setTimeout(() => setMounted(true), 10);
      return () => window.clearTimeout(id);
    }
    setMounted(false);
    setQuery('');
    setResults([]);
    setSelectedSlug(null);
    return undefined;
  }, [isOpen, openedFromSlug]);

  // When the drawer shows the list view, focus the search input.
  useEffect(() => {
    if (!isOpen || selectedSlug) return;
    const id = window.setTimeout(() => {
      const input = searchInputRef.current?.querySelector('input');
      if (input) input.focus();
    }, 60);
    return () => window.clearTimeout(id);
  }, [isOpen, selectedSlug]);

  const knownSlugs = useMemo(
    () => new Set(HELP_ARTICLES.map((a) => a.slug)),
    [],
  );

  const pageSlugs = useMemo(
    () => helpSlugsForPath(location.pathname, knownSlugs),
    [location.pathname, knownSlugs],
  );

  const pageArticles = useMemo(() => {
    const bySlug = new Map(HELP_ARTICLES.map((a) => [a.slug, a] as const));
    const out: HelpArticleMeta[] = [];
    for (const slug of pageSlugs) {
      const a = bySlug.get(slug);
      if (a) out.push(a);
    }
    return out;
  }, [pageSlugs]);

  const searchArticles = useMemo(() => {
    const bySlug = new Map(HELP_ARTICLES.map((a) => [a.slug, a] as const));
    return results
      .map((r) => bySlug.get(r.slug))
      .filter((a): a is HelpArticleMeta => Boolean(a));
  }, [results]);

  const selectedArticle = useMemo(() => {
    if (!selectedSlug) return null;
    return HELP_ARTICLES.find((a) => a.slug === selectedSlug) ?? null;
  }, [selectedSlug]);

  if (typeof document === 'undefined') return null;
  if (!isOpen) return null;

  const showSearchResults = query.trim().length > 0;

  const displayedPageArticles = pageArticles.slice(0, MAX_CONTEXTUAL_ARTICLES);
  const hasMorePageArticles = pageArticles.length > MAX_CONTEXTUAL_ARTICLES;

  // Open an article in place rather than routing away — this is the whole point
  // of the in-drawer reader.
  const openArticle = (slug: string) => setSelectedSlug(slug);

  const backToList = () => setSelectedSlug(null);

  const goToFullPage = (slug: string) => {
    closeDrawer();
    navigate(`/help/${slug}`);
  };

  const goToAllHelp = () => {
    closeDrawer();
    navigate('/help');
  };

  const drawerTransform = reduced
    ? 'translate-x-0'
    : mounted
      ? 'translate-x-0'
      : 'translate-x-full';

  const backdropOpacity = mounted ? 'opacity-100' : 'opacity-0';

  return createPortal(
    <div
      role="dialog"
      aria-label="Contextual help"
      aria-modal="false"
      className="fixed inset-0 z-50"
    >
      <button
        type="button"
        aria-label="Close help"
        onClick={closeDrawer}
        className={`absolute inset-0 bg-dash-bg/40 transition-opacity duration-200 ${backdropOpacity}`}
      />
      <aside
        className={`absolute right-0 top-0 h-full w-full max-w-sm md:w-96 bg-dash-surface border-l border-dash-border shadow-xl flex flex-col transition-transform duration-200 ease-out ${drawerTransform}`}
        data-help-drawer="true"
        data-help-view={selectedArticle ? 'article' : 'list'}
      >
        {selectedArticle ? (
          <>
            <header className="h-12 px-2 border-b border-dash-border flex items-center justify-between shrink-0">
              <button
                type="button"
                onClick={backToList}
                aria-label="Back to help list"
                className="inline-flex items-center gap-1 px-2 py-1 rounded text-sm text-dash-secondary hover:text-dash-primary hover:bg-dash-border/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/60"
              >
                <IconChevronLeft size={16} />
                <span>Help</span>
              </button>
              <button
                type="button"
                onClick={closeDrawer}
                aria-label="Close help"
                className="p-1 rounded text-dash-secondary hover:text-dash-primary hover:bg-dash-border/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/60"
              >
                <IconX size={16} />
              </button>
            </header>
            <div className="flex-1 overflow-y-auto px-4 py-4">
              <Suspense fallback={<SkeletonCard />}>
                <HelpArticle
                  key={selectedArticle.slug}
                  article={selectedArticle}
                  onSelectArticle={openArticle}
                  compact
                />
              </Suspense>
            </div>
            <footer className="h-12 px-4 border-t border-dash-border flex items-center justify-end shrink-0">
              <button
                type="button"
                onClick={() => goToFullPage(selectedArticle.slug)}
                className="inline-flex items-center gap-1 text-xs text-accent hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/60 rounded"
              >
                Open full page
                <IconExternalLink size={12} />
              </button>
            </footer>
          </>
        ) : (
          <>
            <header className="h-12 px-4 border-b border-dash-border flex items-center justify-between shrink-0">
              <div className="flex items-center gap-2">
                <IconHelp size={16} className="text-accent" />
                <span className="text-sm font-medium text-dash-primary">Help for this page</span>
              </div>
              <button
                type="button"
                onClick={closeDrawer}
                aria-label="Close help"
                className="p-1 rounded text-dash-secondary hover:text-dash-primary hover:bg-dash-border/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/60"
              >
                <IconX size={16} />
              </button>
            </header>
            <div className="px-4 py-3 border-b border-dash-border" ref={searchInputRef}>
              <HelpSearchInput
                value={query}
                onChange={setQuery}
                onResults={setResults}
                autoFocus
              />
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-2">
              {showSearchResults ? (
                searchArticles.length === 0 ? (
                  <p className="text-xs text-dash-muted">No matches.</p>
                ) : (
                  searchArticles.map((a) => (
                    <ArticleCard key={a.slug} article={a} onSelect={() => openArticle(a.slug)} />
                  ))
                )
              ) : pageArticles.length === 0 ? (
                <EmptyState title="No help available for this page yet" />
              ) : (
                <>
                  {displayedPageArticles.map((a) => (
                    <ArticleCard key={a.slug} article={a} onSelect={() => openArticle(a.slug)} />
                  ))}
                  {hasMorePageArticles ? (
                    <button
                      type="button"
                      onClick={goToAllHelp}
                      className="w-full text-left text-xs text-accent hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/60 rounded px-1 py-1"
                    >
                      View all {pageArticles.length} related articles →
                    </button>
                  ) : null}
                </>
              )}
            </div>
            <footer className="h-12 px-4 border-t border-dash-border flex items-center justify-end shrink-0">
              <button
                type="button"
                onClick={goToAllHelp}
                className="text-xs text-accent hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/60 rounded"
              >
                View all help
              </button>
            </footer>
          </>
        )}
      </aside>
    </div>,
    document.body,
  );
}
